import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { permissionsListService } from '../services/permissionsList.service';
import type { PermissionAuditLog, PermissionFilters, PermissionImpact, PermissionModule, PermissionRole } from '../types/permissionsList.types';

const initialFilters: PermissionFilters = {
  query: '',
  module: 'all',
  risk: 'all',
};

export function usePermissionsList() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const [filters, setFilters] = useState<PermissionFilters>(initialFilters);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [pendingImpact, setPendingImpact] = useState<PermissionImpact | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [rollbackId, setRollbackId] = useState<string | null>(null);

  const rolesQuery = useQuery({
    queryKey: ['settings', 'roles', 'permissions-matrix'],
    queryFn: permissionsListService.getRoles,
  });

  const usersQuery = useQuery({
    queryKey: ['settings', 'permissions', 'users', companyId],
    queryFn: () => permissionsListService.getUsers(companyId),
  });

  const assignmentsQuery = useQuery({
    queryKey: ['settings', 'permissions', 'assignments'],
    queryFn: permissionsListService.getAssignments,
  });

  const auditQuery = useQuery({
    queryKey: ['settings', 'permissions', 'audit'],
    queryFn: permissionsListService.getRecentAuditLogs,
  });

  const modules = useMemo<PermissionModule[]>(() => {
    const search = filters.query.trim().toLowerCase();
    return permissionsListService.getModules()
      .filter((module) => filters.module === 'all' || module.code === filters.module)
      .map((module) => ({
        ...module,
        resources: module.resources
          .map((resource) => ({
            ...resource,
            actions: resource.actions.filter((action) => {
              const matchesRisk = filters.risk === 'all' || action.risk === filters.risk;
              const matchesSearch = !search || [module.name, resource.name, action.name, action.description].some((value) => value.toLowerCase().includes(search));
              return matchesRisk && matchesSearch;
            }),
          }))
          .filter((resource) => resource.actions.length > 0),
      }))
      .filter((module) => module.resources.length > 0);
  }, [filters]);

  const roles = useMemo(() => rolesQuery.data || [], [rolesQuery.data]);
  const users = useMemo(() => usersQuery.data || [], [usersQuery.data]);
  const assignments = useMemo(() => assignmentsQuery.data || [], [assignmentsQuery.data]);
  const selectedUser = useMemo(() => users.find((user) => user.id === selectedUserId) || users[0] || null, [selectedUserId, users]);

  const effectiveGroups = useMemo(
    () => permissionsListService.getEffectivePermissionGroups(selectedUser, roles, assignments),
    [assignments, roles, selectedUser],
  );

  const requestTogglePermission = (role: PermissionRole, permissionKey: string, enabled: boolean) => {
    setPendingImpact(permissionsListService.buildImpact(role, permissionKey, enabled, users, roles, assignments));
  };

  const confirmTogglePermission = async () => {
    if (!pendingImpact) return;

    setSavingKey(`${pendingImpact.role.id}:${pendingImpact.permissionKey}`);
    try {
      await permissionsListService.setRolePermission({
        role: pendingImpact.role,
        permissionKey: pendingImpact.permissionKey,
        enabled: pendingImpact.nextEnabled,
        actorUserId: profile?.id || null,
        affectedUsers: pendingImpact.affectedUsers,
      });
      await refreshPermissionData();
      toast.success('Đã cập nhật quyền');
      setPendingImpact(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Không thể cập nhật quyền');
    } finally {
      setSavingKey(null);
    }
  };

  const rollbackAuditLog = async (log: PermissionAuditLog) => {
    setRollbackId(log.id);
    try {
      await permissionsListService.rollbackPermissionLog(log, profile?.id || null);
      await refreshPermissionData();
      toast.success('Đã khôi phục quyền từ nhật ký');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Không thể khôi phục quyền');
    } finally {
      setRollbackId(null);
    }
  };

  const refreshPermissionData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles'] }),
      queryClient.invalidateQueries({ queryKey: ['settings', 'permissions', 'audit'] }),
    ]);
  };

  return {
    modules,
    allModules: permissionsListService.getModules(),
    roles,
    users,
    selectedUser,
    selectedUserId,
    effectiveGroups,
    auditLogs: auditQuery.data || [],
    filters,
    pendingImpact,
    stats: permissionsListService.getStats(roles, users, assignments),
    isLoading: rolesQuery.isLoading || usersQuery.isLoading || assignmentsQuery.isLoading,
    isAuditLoading: auditQuery.isLoading,
    savingKey,
    rollbackId,
    setFilters,
    setSelectedUserId,
    setPendingImpact,
    requestTogglePermission,
    confirmTogglePermission,
    rollbackAuditLog,
  };
}
